import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DatePicker,
  Flex,
  Form,
  message,
  Radio,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { exportInsights, getSchemas } from "../actions/acc_actions";
import ActiveUserChart from "./insights/ActiveUserChart";
import RewardsByOutletChart from "./insights/RewardsByOutletChart";
import RewardsChart from "./insights/RewardsChart";
import StampsChart from "./insights/StampsChart";
import InsightsSummary from "./InsightsSummary";

const { Title, Text } = Typography;

export default function InsightsForm() {
  const { businessSlug, businessUrn } = useParams();
  const [form] = Form.useForm();
  // Set default fromDate to one month ago and toDate to current date
  const [fromDate, setFromDate] = useState(
    dayjs().subtract(1, "month").format("YYYY-MM-DD"),
  );
  const [toDate, setToDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [exportLoading, setExportLoading] = useState(false);

  const [selectedSchema, setSelectedSchema] = useState();
  const { isLoading: schemasLoading, data: schemasData } = useQuery({
    queryKey: ["schemas", businessUrn],
    queryFn: () => getSchemas(businessUrn, "STAMP_CARD"),
    enabled: businessUrn ? true : false,
  });
  const schemas = schemasData?.data?.body || [];
  // transform schemas -> radio options
  const options = useMemo(() => {
    return schemas.map((schema) => ({
      label: schema.name,
      value: schema.slug,
    }));
  }, [schemas]);
  // select first schema automatically
  useEffect(() => {
    if (!selectedSchema && options.length > 0) {
      setSelectedSchema(options[0].value);
    }
  }, [options, selectedSchema]);
  const noSchemas = !schemasLoading && schemas.length === 0;

  const onSubmit = () => {
    const values = form.getFieldsValue();
    const { fromDate: formFromDate, toDate: formToDate } = values;

    if (!formFromDate || !formToDate) {
      message.error("Please select both From and To dates.");
      return;
    }

    // Update state with formatted dates
    const formattedFromDate = formFromDate.format("YYYY-MM-DD");
    const formattedToDate = formToDate.format("YYYY-MM-DD");
    setFromDate(formattedFromDate);
    setToDate(formattedToDate);
  };

  //  Export handler
  const onExport = async () => {
    try {
      setExportLoading(true);

      const values = form.getFieldsValue();
      const { fromDate: formFromDate, toDate: formToDate } = values;

      const exportFrom = formFromDate
        ? formFromDate.format("YYYY-MM-DD")
        : fromDate;
      const exportTo = formToDate ? formToDate.format("YYYY-MM-DD") : toDate;

      const params = {
        loyalty_scheme_slug: businessSlug + "-stampcard",
        from: exportFrom,
        to: exportTo,
        refresh: true,
      };

      const result = await exportInsights(params);

      if (result.success) {
        message.success("Export downloaded successfully");
      } else {
        message.error("Failed to export insights");
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="insights-form">
      {/* Scheme selector */}
      {!noSchemas && (
        <Flex align="center" gap={16} wrap="wrap">
          <Text strong>your insightsfor:</Text>

          <Radio.Group
            options={options}
            value={selectedSchema}
            onChange={(e) => setSelectedSchema(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          />
        </Flex>
      )}
      <InsightsSummary selectedSchema={selectedSchema} />
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fromDate: dayjs().subtract(1, "month"),
          toDate: dayjs(),
        }}
      >
        <div className="form-row">
          <Form.Item
            className="date-picker"
            name="fromDate"
            label="From Date"
            rules={[{ required: true, message: "Please select From date" }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            className="date-picker"
            name="toDate"
            label="To Date"
            rules={[{ required: true, message: "Please select To date" }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item className="insights-form-btns">
            <Space>
              <Button
                style={{ width: "16rem" }}
                type="primary"
                variant="solid"
                onClick={onSubmit}
              >
                Submit
              </Button>
              <Button
                onClick={() => {
                  form.resetFields();
                  setFromDate(
                    dayjs().subtract(1, "month").format("YYYY-MM-DD"),
                  );
                  setToDate(dayjs().format("YYYY-MM-DD"));
                }}
              >
                Reset
              </Button>
              <Button
                style={{ width: "16rem" }}
                type="primary"
                variant="solid"
                icon={<FiDownload />}
                loading={exportLoading}
                onClick={onExport}
              >
                Export
              </Button>
            </Space>
          </Form.Item>
        </div>
      </Form>
      <ActiveUserChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
      <StampsChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
      <RewardsChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
      <RewardsByOutletChart
        selectedSchema={selectedSchema}
        dateFrom={fromDate}
        dateTo={toDate}
      />
    </div>
  );
}
